import { useQueryClient } from "@tanstack/react-query";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useReportUnexpectedNull } from "@/hooks/useReportUnexpectedNull";
import { useRollbackPlacement } from "@/hooks/mutations/useRollbackPlacement";
import { useRemovePlacement } from "@/hooks/sets/useRemovePlacement";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import {
  candidacyStatusToLabel,
  defaultPlacementQueryParams,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyPlacement, CandidacyStatus, PlacementStatus } from "@/types";

// Gather side effects of rolling back
const getSideEffects = (
  fromStatus: CandidacyStatus,
  toStatus: CandidacyStatus,
): string[] => {
  const effects: string[] = [];
  effects.push(
    toStatus === CandidacyStatus.OFFER
      ? "The placement reverts to offered. Offer details are kept."
      : "The placement will be deleted.",
  );
  // The requisition is only marked filled once a placement is accepted, so it
  // only needs to reopen when rolling back out of placed.
  if (fromStatus === CandidacyStatus.PLACED) {
    effects.push(
      "The requisition will be reopened back to the live or backfill status.",
    );
  }
  return effects;
};

export const RollbackDialog = ({
  open,
  onOpenChange,
  onStatusChanged,
  placements,
  toStatus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChanged?: (status: CandidacyStatus) => void;
  placements: CandidacyPlacement[];
  toStatus: CandidacyStatus;
}) => {
  /* Contexts */
  const { candidacyId } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });

  /* Database Updates */
  const { mutate: rollbackPlacement } = useRollbackPlacement();

  /* Cache Updates */
  const queryClient = useQueryClient();
  const removePlacement = useRemovePlacement();
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setPlacement = useSetPlacement();

  /* Misc Hooks */
  const reportUnexpectedNull = useReportUnexpectedNull();

  // The placement to roll back is the active one — offered if on offer,
  // accepted if placed.
  const activePlacement = placements.find(
    (placement) =>
      placement.status === PlacementStatus.OFFERED ||
      placement.status === PlacementStatus.ACCEPTED,
  );

  /* Handlers */
  const handleConfirm = () => {
    if (!candidacyInfo || !activePlacement) {
      reportUnexpectedNull(
        "RollbackDialog.handleConfirm",
        !candidacyInfo ? "candidacyInfo" : "activePlacement",
      );
      return;
    }

    mixpanelUtils.trackButtonClick("Rollback placement", MIXPANEL_SCREENS.JOB, {
      from_status: candidacyInfo.status,
      to_status: toStatus,
    });

    rollbackPlacement(
      { placementId: activePlacement.id, toCandidacyStatus: toStatus },
      {
        onSuccess: (rolledBackPlacement) => {
          // The placement is either deleted (then removed) or reverted to
          // offered (then updated) depending on the target status.
          if (rolledBackPlacement.deletedAt) {
            removePlacement({
              id: rolledBackPlacement.id,
              placementQueryParams: defaultPlacementQueryParams,
            });
          } else {
            setPlacement({
              updatedPlacement: rolledBackPlacement,
              placementQueryParams: defaultPlacementQueryParams,
            });
          }
          // Difficult to correctly update cache for reqs here so just invalidate
          void queryClient.invalidateQueries({
            queryKey: ["requisitions", candidacyInfo.jobId],
          });
          setCandidacyInAllLists({
            candidacyId,
            lastCandidacyStatusChange: {
              createdAt: new Date().toISOString(),
              fromStatus: candidacyInfo.status,
              toStatus,
              statusChangeReason: null,
              byAgent: false,
            },
          });
          setCandidacyInfo({
            candidacyId,
            status: toStatus,
            lastStatusChangeByAgent: false,
          });
          onStatusChanged?.(toStatus);
          onOpenChange(false);
        },
      },
    );
  };

  /* Render */
  if (!candidacyInfo) return null;

  const sideEffects = getSideEffects(candidacyInfo.status, toStatus);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move back from {candidacyStatusToLabel[candidacyInfo.status]} to{" "}
            {candidacyStatusToLabel[toStatus]}?
          </DialogTitle>
        </DialogHeader>
        <Column className="gap-1">
          {sideEffects.map((effect) => (
            <Text key={effect} variant="muted">
              • {effect}
            </Text>
          ))}
        </Column>
        <Row className="justify-end gap-2">
          <Button
            className="rounded-full"
            onClick={() => {
              onOpenChange(false);
            }}
            variant="outlineSecondary"
          >
            Cancel
          </Button>
          <Button className="rounded-full" onClick={handleConfirm}>
            Move back to {candidacyStatusToLabel[toStatus]}
          </Button>
        </Row>
      </DialogContent>
    </Dialog>
  );
};
